
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Discount } from "@/types/discount";
import { useDebounce } from "@/hooks/use-debounce";

// Define interfaces for the related data responses
interface ClientData {
  nama_sekolah: string;
}

interface PlanData {
  name: string;
}

interface ProductData {
  name: string;
}

export const useDiscounts = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [discountToDelete, setDiscountToDelete] = useState<Discount | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { toast } = useToast();
  
  const debouncedSearch = useDebounce(searchQuery, 500);
  const PAGE_SIZE = 10;

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_filtered_discounts', {
        search_query: debouncedSearch,
        page_number: page,
        page_size: PAGE_SIZE
      });

      if (error) throw error;

      const { discounts: discountsData, total_count } = data[0];
      
      // Calculate total pages
      setTotalPages(Math.ceil(total_count / PAGE_SIZE));
      
      // Ensure status is correctly typed
      const typedDiscounts: Discount[] = (discountsData as any[])?.map(discount => ({
        ...discount,
        status: discount.status as 'active' | 'inactive',
        school_name: discount.school_name || '-',
        plan_name: discount.plan_name || '-',
        product_name: discount.product_name || '-'
      })) || [];
      
      setDiscounts(typedDiscounts);
    } catch (error) {
      console.error('Error fetching discounts:', error);
      toast({
        title: "Error",
        description: "Failed to fetch discounts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('discounts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Discount deleted successfully",
      });
      
      fetchDiscounts();
    } catch (error) {
      console.error('Error deleting discount:', error);
      toast({
        title: "Error",
        description: "Failed to delete discount",
        variant: "destructive",
      });
    } finally {
      setDiscountToDelete(null);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, [debouncedSearch, page]);

  return {
    discounts,
    loading,
    discountToDelete,
    setDiscountToDelete,
    handleDelete,
    fetchDiscounts,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    totalPages,
  };
};
